import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2, Beer, Clock, CheckCircle, RefreshCw } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';

const ORDER_STATUSES = ['pending', 'preparing', 'ready', 'delivered', 'cancelled'];
const STATUS_LABELS = {
  pending: 'Afventer',
  preparing: 'Forberedes',
  ready: 'Klar',
  delivered: 'Leveret',
  cancelled: 'Annulleret',
};
const STATUS_STYLES = {
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  preparing: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  ready: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  delivered: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
      {status === 'ready' || status === 'delivered' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function AdminDrinks() {
  const [drinks, setDrinks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderSearch, setOrderSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    category: 'Soda',
    description: '',
    active: true,
  });

  const load = () => {
    apiGet('/api/drinks').then(r => setDrinks(r.data.drinks || []));
    apiGet('/api/drink-orders').then(r => setOrders(r.data.orders || []));
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, []);

  const groupedOrders = ORDER_STATUSES.reduce((acc, status) => {
    const s = orderSearch.toLowerCase();
    acc[status] = orders.filter((order) => {
      if (order.status !== status) return false;
      return !s ||
        (order.guest_name || '').toLowerCase().includes(s) ||
        (order.table_name || '').toLowerCase().includes(s) ||
        (order.items || []).some((item) => (item.name || '').toLowerCase().includes(s));
    });
    return acc;
  }, {});
  const drinkTotals = orders.reduce((acc, order) => {
    (order.items || []).forEach((item) => {
      acc[item.name] = (acc[item.name] || 0) + (item.quantity || 1);
    });
    return acc;
  }, {});
  const topDrink = Object.entries(drinkTotals).sort((a, b) => b[1] - a[1])[0];
  const ordersByHour = orders.reduce((acc, order) => {
    if (!order.created_at) return acc;
    const hour = new Date(order.created_at).getHours();
    acc[hour] = (acc[hour] || 0) + 1;
    return acc;
  }, {});
  const busiestHour = Object.entries(ordersByHour).sort((a, b) => b[1] - a[1])[0];

  const resetForm = () => {
    setEditing(null);
    setForm({ name: '', category: 'Soda', description: '', active: true });
  };

  const editDrink = (drink) => {
    setEditing(drink.id);
    setForm({
      name: drink.name || '',
      category: drink.category || 'Soda',
      description: drink.description || '',
      active: drink.active !== false,
    });
  };

  const saveDrink = async () => {
    if (!form.name.trim()) {
      alert('Navn er påkrævet');
      return;
    }

    if (editing) {
      await apiPut(`/api/drinks/${editing}`, form);
    } else {
      await apiPost('/api/drinks', form);
    }

    resetForm();
    load();
  };

  const deleteDrink = async (id) => {
    if (!window.confirm('Slet denne drink?')) return;
    await apiDelete(`/api/drinks/${id}`);
    load();
  };

  const updateOrderStatus = async (id, status) => {
    if (status === 'delivered' && !window.confirm('Markér denne ordre som leveret?')) return;
    await apiPut(`/api/drink-orders/${id}/status`, { status });
    load();
  };

  return (
    <div className="max-w-7xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-slate-50 flex items-center gap-2">
            <Beer className="w-6 h-6 text-amber-400" />
            Barordrer
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Administrér drinks og barbestillinger.
          </p>
        </div>

        <Button onClick={resetForm}>
          <Plus className="w-4 h-4 mr-2" />
          Ny drink
        </Button>
        <Button variant="outline" onClick={load} className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-400">
            Drinks
          </h2>

          {drinks.length === 0 ? (
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-6 text-slate-400 text-sm">
              Ingen drinks endnu.
            </div>
          ) : (
            drinks.map((drink) => (
              <div key={drink.id} className="bg-slate-800/40 border border-slate-700 rounded-xl p-5 flex justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-slate-100 font-medium">{drink.name}</h3>
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-400">
                      {drink.category}
                    </span>
                    {drink.active === false && (
                      <span className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400">
                        Skjult
                      </span>
                    )}
                  </div>
                  {drink.description && (
                    <p className="text-sm text-slate-400">{drink.description}</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <button onClick={() => editDrink(drink)} className="text-amber-400 hover:text-amber-300">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteDrink(drink.id)} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-6 h-fit">
          <h2 className="text-lg text-slate-100 mb-5">
            {editing ? 'Rediger drink' : 'Ny drink'}
          </h2>

          <div className="space-y-4">
            <input
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-slate-100"
              placeholder="Navn"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />

            <select
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-slate-100"
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
            >
              <option>Soda</option>
              <option value="Beer">Øl</option>
              <option value="Wine">Vin</option>
              <option>Drink</option>
              <option>Shot</option>
              <option value="Water">Vand</option>
            </select>

            <textarea
              rows={3}
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-slate-100"
              placeholder="Beskrivelse"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
            />

            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={form.active}
                onChange={e => setForm({ ...form, active: e.target.checked })}
              />
              Aktiv
            </label>

            <Button className="w-full" onClick={saveDrink}>
              Gem drink
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-500">Mest bestilt</p>
            <p className="text-sm text-slate-200 mt-1">{topDrink ? `${topDrink[0]} (${topDrink[1]})` : 'Ingen ordrer endnu'}</p>
          </div>
          <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-500">Travleste time</p>
            <p className="text-sm text-slate-200 mt-1">{busiestHour ? `${busiestHour[0]}:00 (${busiestHour[1]})` : 'Ingen ordrer endnu'}</p>
          </div>
          <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-500">Aktiv kø</p>
            <p className="text-sm text-slate-200 mt-1">{(groupedOrders.pending?.length || 0) + (groupedOrders.preparing?.length || 0) + (groupedOrders.ready?.length || 0)} ordrer</p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-amber-400">
            Barkø
          </h2>
          <input
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
            placeholder="Søg i ordrer..."
            className="bg-slate-900/50 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
          />
        </div>

        <div className="grid xl:grid-cols-5 md:grid-cols-2 gap-4">
          {ORDER_STATUSES.map(status => (
            <div key={status} className="bg-slate-900/30 border border-slate-700/50 rounded-xl p-4 min-h-[180px]">
              <div className="flex items-center justify-between mb-4">
                <StatusBadge status={status} />
                <span className="text-xs text-slate-500">{groupedOrders[status].length}</span>
              </div>

              <div className="space-y-3">
                {groupedOrders[status].length === 0 ? (
                  <p className="text-xs text-slate-600">Ingen ordrer.</p>
                ) : groupedOrders[status].map(order => (
                  <div key={order.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <div className="flex justify-between gap-3 mb-3">
                      <div>
                        <h3 className="text-slate-100 font-medium">{order.guest_name}</h3>
                        <p className="text-xs text-slate-400">
                          {order.table_name ? `Bord: ${order.table_name}` : 'Intet bord'}
                        </p>
                      </div>
                      <span className="text-xs text-slate-500 text-right">
                        <span className="block">{formatRelativeTime(order.created_at)}</span>
                        <span className="block text-slate-600">{order.created_at ? new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </span>
                    </div>

                    <div className="space-y-1 mb-4">
                      {order.items?.map((item, idx) => (
                        <p key={idx} className="text-sm text-slate-300">
                          {item.quantity}x {item.name}
                        </p>
                      ))}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {status === 'pending' && (
                        <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.id, 'preparing')}>
                          Start
                        </Button>
                      )}
                      {status !== 'ready' && status !== 'delivered' && (
                        <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.id, 'ready')}>
                          Klar
                        </Button>
                      )}
                      {status !== 'delivered' && (
                        <Button size="sm" onClick={() => updateOrderStatus(order.id, 'delivered')}>
                          Leveret
                        </Button>
                      )}
                      {status !== 'delivered' && status !== 'cancelled' && (
                        <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.id, 'cancelled')}>
                          Annuller
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
